import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Building2, Briefcase, Crown, Search } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberDirectory = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, isPremium } = useAuth();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobFunctions, setJobFunctions] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isPremium()) {
      toast.error('Member directory requires Premium membership');
      navigate('/membership');
      return;
    }
    fetchData();
  }, [user, authLoading, isPremium, navigate]);

  useEffect(() => {
    filterMembers();
  }, [members, searchTerm]);

  const fetchData = async () => {
    try {
      const [membersRes, jobsRes, typesRes] = await Promise.all([
        axios.get(`${API}/members/directory`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/job-functions`),
        axios.get(`${API}/company-types`),
      ]);
      setMembers(membersRes.data);
      setFilteredMembers(membersRes.data);
      setJobFunctions(jobsRes.data);
      setCompanyTypes(typesRes.data);
    } catch (error) {
      toast.error('Error loading member directory');
    } finally {
      setLoading(false);
    }
  };

  const filterMembers = () => {
    if (!searchTerm) {
      setFilteredMembers(members);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = members.filter(m => 
      m.full_name?.toLowerCase().includes(term) ||
      m.organization?.toLowerCase().includes(term) ||
      m.email?.toLowerCase().includes(term)
    );
    setFilteredMembers(filtered);
  };

  const getJobLabel = (value) => {
    const job = jobFunctions.find(j => j.value === value);
    return job ? job.label : value;
  };

  const getCompanyLabel = (value) => {
    const type = companyTypes.find(t => t.value === value);
    return type ? type.label : value;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <p className="overline text-gold mb-4">Premium Feature</p>
            <h1 className="text-3xl md:text-4xl flex items-center gap-3">
              <Users className="w-8 h-8 text-gold" />
              Member Directory
            </h1>
          </div>
          <div className="mt-6 md:mt-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64 rounded-sm"
                data-testid="search-members-input"
              />
            </div>
          </div>
        </div>

        <p className="text-muted-foreground mb-8">
          {filteredMembers.length} premium member{filteredMembers.length !== 1 ? 's' : ''} found
        </p>

        {filteredMembers.length === 0 ? (
          <div className="text-center py-16 border border-border/50 bg-card">
            <Users className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl mb-2">No Members Found</h3>
            <p className="text-muted-foreground">
              {searchTerm ? 'Try adjusting your search.' : 'Be the first premium member!'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member, index) => (
              <div 
                key={member.id} 
                className="border border-border/50 p-6 bg-card"
                data-testid={`member-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                    <span className="text-gold text-lg font-semibold">
                      {member.full_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-gold">
                    <Crown className="w-3 h-3" /> Premium
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold mb-1">{member.full_name}</h3>
                
                {member.organization && (
                  <p className="text-muted-foreground text-sm flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4" />
                    {member.organization}
                  </p>
                )}
                
                <p className="text-muted-foreground text-sm flex items-center gap-2 mb-2">
                  <Briefcase className="w-4 h-4" />
                  {getJobLabel(member.job_function)}
                </p>
                
                <p className="text-xs text-muted-foreground mt-4">
                  {getCompanyLabel(member.company_type)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberDirectory;
